<template>
  <v-card
    elevation="0"
    class="modern-agencies-card rounded-3xl overflow-hidden"
    :class="{ 'dark-mode': isDarkMode }"
  >
    <!-- Header Section with Gradient Background -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-icon">
          <v-icon size="32" class="icon-agencies">mdi-office-building</v-icon>
        </div>
        <div class="header-text">
          <h1 class="header-title">Agencies Overview</h1>
          <p class="header-subtitle">Real-time agency statistics and growth analytics</p>
        </div>
        <div class="header-actions">
          <v-btn
            icon
            size="small"
            variant="text"
            class="theme-toggle"
            @click="toggleTheme"
          >
            <v-icon>{{ isDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Chart Section -->
    <div class="chart-section">
  
      <div class="chart-container">
        <div class="chart-wrapper">
          <highcharts
            :options="chartOptions"
            class="modern-chart"
          ></highcharts>
        </div>
      </div>
    </div>

    <!-- Stats Section -->
    <div class="stats-section">
      <div class="stats-grid">
        <div
          v-for="(stat, index) in statsData"
          :key="stat.type"
          class="stat-card"
          :class="[`stat-${stat.type.toLowerCase()}`, { 'animate-in': animateStats }]"
          :style="{ '--delay': `${index * 0.1}s` }"
          @click="goToAgencies(stat.route)"
        >
          <div class="stat-icon">
            <v-icon :color="stat.iconColor" size="24">{{ stat.icon }}</v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-number">
              <span class="counter" :data-target="stat.value">{{ animatedValues[stat.type] || 0 }}</span>
            </div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
          <div class="stat-trend" v-if="stat.trend">
            <v-icon
              :color="stat.trend > 0 ? 'success' : 'error'"
              size="16"
            >
              {{ stat.trend > 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
            </v-icon>
            <!-- <span class="trend-value">{{ Math.abs(stat.trend) }}%</span> -->
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";

export default {
  data() {
    return {
      isDarkMode: false,
      selectedPeriod: '30d',
      animateStats: false,
      animatedValues: {},
      timePeriods: [
        { label: '7D', value: '7d' },
        { label: '30D', value: '30d' },
        { label: '3M', value: '3m' },
        { label: '1Y', value: '1y' }
      ],
      chartOptions: {
        chart: {
          type: "area",
          backgroundColor: 'transparent',
          borderRadius: 16,
          marginBottom: 30,
          marginLeft: 60,
          marginRight: 30,
          marginTop: 40,
          style: {
            fontFamily: 'Inter, system-ui, sans-serif'
          }
        },
        credits: {
          enabled: false
        },
        title: {
          text: null
        },
        xAxis: {
          type: "datetime",
          gridLineWidth: 0,
          lineWidth: 0,
          tickWidth: 0,
          labels: {
            style: {
              color: '#64748b',
              fontSize: '12px'
            },
            formatter: function() {
              return moment(this.value).format("MMM DD");
            }
          }
        },
        yAxis: {
          title: { text: null },
          gridLineWidth: 1,
          gridLineColor: '#f1f5f9',
          labels: {
            style: {
              color: '#64748b',
              fontSize: '12px'
            }
          }
        },
        plotOptions: {
          area: {
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, 'rgba(52, 232, 158, 0.3)'],
                [1, 'rgba(52, 232, 158, 0.05)']
              ]
            },
            marker: {
              radius: 4,
              fillColor: '#34e89e',
              lineWidth: 2,
              lineColor: '#ffffff'
            },
            lineWidth: 3,
            states: {
              hover: {
                lineWidth: 3
              }
            },
            threshold: null
          }
        },
        series: [
          {
            name: "Agencies",
            data: this.agencyGraphDatas,
            color: "#34e89e",
            marker: {
              enabled: true
            }
          }
        ],
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: 'transparent',
          borderRadius: 8,
          style: {
            color: '#ffffff',
            fontSize: '13px'
          },
          formatter: function() {
            return `<b>${moment(this.x).format("MMM DD, YYYY")}</b><br/>
                    Agencies: <b>${this.y}</b>`;
          }
        },
        legend: {
          enabled: false
        }
      }
    };
  },
  computed: {
    ...mapGetters("adminDashboard", ["getAgencyCount", "agencyGraphData"]),
    agencyGraphDatas() {
      return this.agencyGraphData || [];
    },
    statsData() {
      return [
        {
          type: 'Registered',
          label: 'Total Registered',
          value: this.getAgencyCount?.registeredAgencies || 0,
          icon: 'mdi-office-building',
          iconColor: 'primary',
          route: 'all',
          trend: 8.5
        },
        {
          type: 'Pending',
          label: 'Awaiting Approval',
          value: this.getAgencyCount?.pendingAgencies || 0,
          icon: 'mdi-clock-outline',
          iconColor: 'warning',
          route: 'Pending',
          trend: -2.3
        },
        {
          type: 'Approved',
          label: 'Active Agencies',
          value: this.getAgencyCount?.availableAgencies || 0,
          icon: 'mdi-check-circle',
          iconColor: 'success',
          route: 'Approved',
          trend: 12.8
        },
        {
          type: 'Rejected',
          label: 'Rejected',
          value: this.getAgencyCount?.rejectedAgencies || 0,
          icon: 'mdi-close-circle',
          iconColor: 'error',
          route: 'Rejected',
          trend: -5.2
        }
      ];
    }
  },
  methods: {
    ...mapActions("adminDashboard", [
      "fetchAgencyCountDetails",
      "fetchAgencyGraphDetails"
    ]),
    goToAgencies(type) {
      this.$router.push({ name: "admin-agencies", query: { type } });
    },
    toggleTheme() {
      this.isDarkMode = !this.isDarkMode;
      document.documentElement.setAttribute('data-theme', this.isDarkMode ? 'dark' : 'light');
    },
    animateNumbers() {
      this.statsData.forEach(stat => {
        this.animateCounter(stat.type, stat.value);
      });
    },
    animateCounter(type, target) {
      const duration = 1000;
      const start = Date.now();
      const startValue = 0;
      
      const animate = () => {
        const progress = Math.min((Date.now() - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // Ease out cubic
        const current = Math.floor(startValue + (target - startValue) * eased);
        
        this.$set(this.animatedValues, type, current);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      animate();
    }
  },
  watch: {
    agencyGraphData(newData) {
      this.chartOptions.series[0].data = newData;
    },
    getAgencyCount: {
      handler() {
        this.$nextTick(() => {
          this.animateNumbers();
        });
      },
      deep: true
    }
  },
  mounted() {
    this.fetchAgencyCountDetails();
    this.fetchAgencyGraphDetails();
    
    // Trigger animations after component is mounted
    setTimeout(() => {
      this.animateStats = true;
      this.animateNumbers();
    }, 300);
  }
};
</script>

<style scoped>
.modern-agencies-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  max-width: 100%;
  margin: 0;
   border-radius: 20px;
}

.modern-agencies-card.dark-mode {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-color: rgba(255, 255, 255, 0.1);
}

/* Header Section */
.header-section {
background: linear-gradient(135deg, #0f3443 0%, #34e89e 100%);
  padding: 24px;
  position: relative;
  overflow: hidden;
}

.header-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.1)"/></svg>');
  background-size: 30px 30px;
  animation: float 20s infinite linear;
}

@keyframes float {
  0% { transform: translateY(0); }
  100% { transform: translateY(-30px); }
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 1;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  padding: 12px;
  border-radius: 16px;
  backdrop-filter: blur(10px);
}

.icon-agencies {
  color: white !important;
}

.header-text {
  flex: 1;
}

.header-title {
  color: white;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.header-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin: 4px 0 0 0;
  line-height: 1.4;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.theme-toggle {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
  color: white !important;
}

/* Chart Section */
.chart-section {
  padding: 24px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
}

.chart-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.dark-mode .chart-title {
  color: #f1f5f9;
}

.chart-controls {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.period-chip {
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
}

.period-chip:hover {
  transform: translateY(-1px);
}

.chart-container {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
}

.dark-mode .chart-container {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.1);
}

.chart-wrapper {
  min-height: 200px;
  height: 100%;
  width: 100%;
}

.modern-chart {
  height: 100%;
  width: 100%;
}

/* Stats Section */
.stats-section {
  padding: 0 24px 24px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 20px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(20px);
}

.stat-card.animate-in {
  animation: slideUp 0.6s ease forwards;
  animation-delay: var(--delay);
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dark-mode .stat-card {
  background: rgba(0, 0, 0, 0.3);
  border-color: rgba(255, 255, 255, 0.1);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--accent-color), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-card:hover::before {
  opacity: 1;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  background: rgba(112, 36, 196, 0.1);
  padding: 12px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 48px;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.dark-mode .stat-number {
  color: #f1f5f9;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  font-weight: 500;
}

.dark-mode .stat-label {
  color: #94a3b8;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
}

.trend-value {
  color: inherit;
}

/* Insights Section */
.insights-section {
  padding: 0 24px 24px;
}

.insights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.insight-card {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;
}

.dark-mode .insight-card {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.1);
}

.insight-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.insight-icon {
  background: rgba(112, 36, 196, 0.1);
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
}

.insight-content {
  flex: 1;
}

.insight-title {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 2px;
  font-weight: 500;
}

.dark-mode .insight-title {
  color: #94a3b8;
}

.insight-value {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.dark-mode .insight-value {
  color: #f1f5f9;
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .header-actions {
    align-self: flex-end;
  }
  
  .chart-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .stat-card {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }
  
  .insights-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .modern-agencies-card {
    margin: 0;
    border-radius: 16px;
  }
  
  .header-section,
  .chart-section,
  .stats-section,
  .insights-section {
    padding: 16px;
  }
  
  .header-title {
    font-size: 20px;
  }
  
  .stat-number {
    font-size: 24px;
  }
  
  .chart-container {
    padding: 16px;
  }
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

::-webkit-scrollbar-thumb {
  background: rgba(112, 36, 196, 0.3);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(112, 36, 196, 0.5);
}
</style>